import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { enterPartnerContext } from '../lib/partnerContext';
import Sidebar from '../components/Sidebar';
import ManagedOnboardingForm from '../components/ManagedOnboardingForm';
import InvitationsPanel, { type InviteRecord } from '../components/InvitationsPanel';
import SlideOver from '../components/SlideOver';
import PartnerClientApprovalMatrix, { compactPartnerMatrix, fullPartnerMatrix } from '../components/clientApprovals/PartnerClientApprovalMatrix';
import { D9_COPY } from '../lib/clientApprovals';

interface PartnerNotifConfig {
  configured: boolean;
  notifyOnGateFailure: boolean;
  notifyOnArtefactApproval: boolean;
  notifyOnPhaseHandoff: boolean;
  notifyOnClientActivity: boolean;
  digestFrequency: string;
  digestTime: string | null;
  enabled: boolean;
  defaults?: Omit<PartnerNotifConfig, 'configured' | 'defaults'>;
}

interface ClientTenant {
  id: string;
  clientTenantId: string;
  deliveryModel: 'SELF_SERVICE' | 'MANAGED';
  tenant: { name: string };
  activeProjectCount: number;
  gateHealth: 'PASSING' | 'FAILING' | 'BLOCKED' | 'NO_GATES';
  failingGateCount: number;
  linkedAt: string;
}

interface ApprovalConfigDefaults {
  requireClientPRDApproval?: boolean;
  requireClientQAApproval?: boolean;
  requireClientDeploymentApproval?: boolean;
  revocationWindowHours?: number;
  acknowledgementWindowHours?: number;
  defaultApprovalMode?: string;
  // #3185 APPROVE.2 2c (ruling D8): artefact type -> required. The three
  // requireClient* keys above are ignored by the server and no longer edited.
  clientApprovalMatrix?: Record<string, boolean>;
}

interface RoleTierOverrides {
  [role: string]: string[];
}

interface GovernanceDefaults {
  defaultClientApprovalConfig: ApprovalConfigDefaults;
  defaultRoleTierOverrides: RoleTierOverrides;
}

const ARTEFACT_TYPE_OPTIONS = [
  { value: 'prd', label: 'PRD' },
  { value: 'sprint_backlog', label: 'Sprint Backlog' },
  { value: 'qa_report', label: 'QA Summary' },
  { value: 'deployment_runbook', label: 'Deployment' },
];

const BUSINESS_ROLES = [
  { key: 'CLIENT_SPONSOR', label: 'Client Sponsor' },
  { key: 'BUSINESS_ANALYST', label: 'Business Analyst' },
  { key: 'SIGN_OFF_AUTHORITY', label: 'Sign-Off Authority' },
  { key: 'END_USER_REP', label: 'End User Rep' },
];

const HEALTH_BADGE: Record<string, { label: string; color: string }> = {
  BLOCKED:  { label: 'Gate blocked',         color: '#ef4444' },
  FAILING:  { label: 'gate(s) failing',      color: '#f59e0b' },
  PASSING:  { label: 'All gates passing',    color: '#22c55e' },
  NO_GATES: { label: 'Awaiting evaluation',  color: '#6b7280' },
};

export default function PartnerPortalPage() {
  const [clients, setClients] = useState<ClientTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifConfig, setNotifConfig] = useState<PartnerNotifConfig | null>(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [invites, setInvites] = useState<InviteRecord[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ clientName: '', clientEmail: '', deliveryModel: 'SELF_SERVICE', overridePermission: 'ALLOWED_WITH_REASON' });
  const [inviteSending, setInviteSending] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendToast, setResendToast] = useState<string | null>(null);
  const [showManagedForm, setShowManagedForm] = useState(false);
  const [applyingDefaultsTo, setApplyingDefaultsTo] = useState<string | null>(null);
  const [applyDefaultsResult, setApplyDefaultsResult] = useState<{ tenantId: string; count: number } | null>(null);
  const [applyDefaultsModal, setApplyDefaultsModal] = useState<{ clientTenantId: string; clientName: string; projectCount: number } | null>(null);
  const [governanceDefaults, setGovernanceDefaults] = useState<GovernanceDefaults | null>(null);
  const [governanceLoading, setGovernanceLoading] = useState(false);
  const [governanceLoadError, setGovernanceLoadError] = useState<string | null>(null);
  const [governanceSaving, setGovernanceSaving] = useState(false);
  const [governanceToast, setGovernanceToast] = useState<string | null>(null);
  // Editable governance state
  const [editApproval, setEditApproval] = useState<ApprovalConfigDefaults>({});
  const [editRoleOverrides, setEditRoleOverrides] = useState<RoleTierOverrides>({});
  const navigate = useNavigate();
  const { user, setSession } = useAuth();

  // #3185 2c (Codex R2 finding 1): the canonical defaults, re-read after every
  // save. A failed load clears them, so nothing is seeded from stale or absent ones.
  const fetchGovernanceDefaults = useCallback(() => {
    setGovernanceLoading(true);
    setGovernanceLoadError(null);
    return apiFetch<GovernanceDefaults>('/partner/config/step3a')
      .then((data) => {
        setGovernanceDefaults(data);
        setEditApproval(data.defaultClientApprovalConfig ?? {});
        setEditRoleOverrides(data.defaultRoleTierOverrides ?? {});
      })
      .catch((err: any) => {
        const message = err.message ?? 'Failed to load governance defaults';
        setGovernanceDefaults(null);
        setGovernanceLoadError(message);
        setError(message);
      })
      .finally(() => setGovernanceLoading(false));
  }, []);

  useEffect(() => {
    // Check wizard completion — redirect if onboarding not done
    apiFetch<{ completedAt: string | null }>('/partner/wizard')
      .then((wizard) => {
        if (!wizard.completedAt) {
          navigate('/partner/onboarding');
          return;
        }
        return apiFetch<{ analytics: any; clients: ClientTenant[] }>('/partner/clients');
      })
      .then((data) => { if (data) setClients(data.clients); })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));

    apiFetch<PartnerNotifConfig>('/partner/notifications/config')
      .then(setNotifConfig)
      .catch(() => {});

    apiFetch<InviteRecord[]>('/partner/clients/invite')
      .then(setInvites)
      .catch(() => {});

    fetchGovernanceDefaults();
  }, [navigate, fetchGovernanceDefaults]);

  const handleCreateInvite = async () => {
    setInviteSending(true);
    try {
      const result = await apiFetch<{ id: string; inviteUrl: string; token: string; expiresAt: string }>(
        '/partner/clients/invite',
        { method: 'POST', body: JSON.stringify(inviteForm) },
      );
      setInvites(prev => [{
        id: result.id,
        clientName: inviteForm.clientName || null,
        clientEmail: inviteForm.clientEmail || null,
        inviteUrl: result.inviteUrl,
        createdAt: new Date().toISOString(),
        expiresAt: result.expiresAt,
        status: 'PENDING',
      }, ...prev]);
      setShowInviteModal(false);
      setInviteForm({ clientName: '', clientEmail: '', deliveryModel: 'SELF_SERVICE', overridePermission: 'ALLOWED_WITH_REASON' });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setInviteSending(false);
    }
  };

  const handleRevokeInvite = async (id: string) => {
    try {
      await apiFetch(`/partner/clients/invite/${id}`, { method: 'DELETE' });
      setInvites(prev =>
        prev.map(inv =>
          inv.id === id
            ? { ...inv, status: 'REVOKED' as const, revokedAt: new Date().toISOString() }
            : inv,
        ),
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleResendInvite = async (invite: InviteRecord) => {
    setResendingId(invite.id);
    try {
      const result = await apiFetch<{ id: string; inviteUrl: string; token: string; expiresAt: string }>(
        '/partner/clients/invite',
        {
          method: 'POST',
          body: JSON.stringify({
            clientName: invite.clientName ?? undefined,
            clientEmail: invite.clientEmail ?? undefined,
            deliveryModel: invite.deliveryModel ?? 'SELF_SERVICE',
            overridePermission: invite.overridePermission ?? 'ALLOWED_WITH_REASON',
          }),
        },
      );
      setInvites(prev => [
        {
          id: result.id,
          clientName: invite.clientName,
          clientEmail: invite.clientEmail,
          deliveryModel: invite.deliveryModel,
          overridePermission: invite.overridePermission,
          inviteUrl: result.inviteUrl,
          createdAt: new Date().toISOString(),
          expiresAt: result.expiresAt,
          revokedAt: null,
          status: 'PENDING',
        },
        ...prev,
      ]);
      setResendToast(`Invitation resent to ${invite.clientEmail ?? 'client'}`);
      setTimeout(() => setResendToast(null), 4000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResendingId(null);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSwitchContext = async (clientTenantId: string) => {
    const result = await apiFetch<{ contextToken: string; tenantId: string; partnerAccountId: string }>(
      `/partner/clients/${clientTenantId}/switch`,
      { method: 'POST' },
    );
    if (!user) return;
    // #1330: sync the scoped token through AuthContext (setSession) so
    // useAuth().token tracks the client context without a full page reload.
    enterPartnerContext(
      { contextToken: result.contextToken, partnerAccountId: result.partnerAccountId, returnPath: '/partner' },
      user,
      setSession,
    );
    navigate('/dashboard');
  };

  const openApplyDefaultsModal = (client: ClientTenant) => {
    setApplyDefaultsModal({
      clientTenantId: client.clientTenantId,
      clientName: client.tenant.name,
      projectCount: client.activeProjectCount,
    });
    // Always re-fetch to ensure modal shows latest server state
    fetchGovernanceDefaults();
  };

  const confirmApplyDefaults = async () => {
    if (!applyDefaultsModal || applyingDefaultsTo) return;
    const { clientTenantId } = applyDefaultsModal;
    setApplyingDefaultsTo(clientTenantId);
    setApplyDefaultsResult(null);
    try {
      const result = await apiFetch<{ applied: boolean; projectsUpdated: number }>(
        `/partner/clients/${clientTenantId}/apply-defaults`,
        { method: 'POST' },
      );
      setApplyDefaultsResult({ tenantId: clientTenantId, count: result.projectsUpdated });
      setApplyDefaultsModal(null);
    } catch (err: any) {
      setError(err.message);
      setApplyDefaultsModal(null);
    } finally {
      setApplyingDefaultsTo(null);
    }
  };

  const handleSaveGovernanceDefaults = async () => {
    setGovernanceSaving(true);
    try {
      // The PATCH answers { updated: true }, not the defaults: re-read them.
      await apiFetch('/partner/config/step3a', {
        method: 'PATCH',
        body: JSON.stringify({
          defaultClientApprovalConfig: editApproval,
          defaultRoleTierOverrides: editRoleOverrides,
        }),
      });
      await fetchGovernanceDefaults();
      setGovernanceToast('Governance defaults updated');
      setTimeout(() => setGovernanceToast(null), 4000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGovernanceSaving(false);
    }
  };

  const toggleRoleArtefact = (role: string, artefact: string) => {
    setEditRoleOverrides((prev) => {
      const current = prev[role] ?? [];
      const next = current.includes(artefact)
        ? current.filter((a) => a !== artefact)
        : [...current, artefact];
      return { ...prev, [role]: next };
    });
  };

  const gateHealthBadge = (health: string, failingCount: number) => {
    const badge = HEALTH_BADGE[health] ?? HEALTH_BADGE.PASSING;
    const label = health === 'FAILING' ? `${failingCount} ${badge.label}` : badge.label;
    return (
      <span style={{ color: badge.color, fontSize: 12 }}>{label}</span>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <main className="flex-1 p-6" style={{ backgroundColor: 'var(--bg)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
                Partner Portal
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                Manage your client engagements
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                + Invite New Client
              </button>
              {!showManagedForm && (
                <button
                  onClick={() => setShowManagedForm(true)}
                  style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                >
                  Set Up Client Workspace
                </button>
              )}
            </div>
          </div>

          {showManagedForm && (
            <div data-testid="managed-form-panel" className="border rounded-lg p-6 mb-6" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}>
              {/* Codex R2 finding 1: the form starts from the loaded defaults
                  only. It mounts once they're in, and keeps its own edits after. */}
              {governanceDefaults ? (
                <ManagedOnboardingForm
                  partnerMatrix={governanceDefaults.defaultClientApprovalConfig?.clientApprovalMatrix}
                  onSuccess={() => {
                    setShowManagedForm(false);
                    apiFetch<{ analytics: any; clients: ClientTenant[] }>('/partner/clients').then(data => setClients(data.clients)).catch(() => {});
                  }}
                  onCancel={() => setShowManagedForm(false)}
                />
              ) : governanceLoadError ? (
                <div data-testid="managed-form-defaults-error">
                  <p className="text-sm mb-3" style={{ color: '#ef4444' }}>
                    Couldn&rsquo;t load your client approval defaults, so a workspace can&rsquo;t be set up yet. {governanceLoadError}
                  </p>
                  <div className="flex gap-3">
                    <button onClick={() => setShowManagedForm(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={() => { void fetchGovernanceDefaults(); }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                      Try again
                    </button>
                  </div>
                </div>
              ) : (
                <p data-testid="managed-form-defaults-loading" className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Loading your client approval defaults&hellip;
                </p>
              )}
            </div>
          )}

          {isLoading && (
            <p style={{ color: 'var(--text-muted)' }}>Loading partner portal...</p>
          )}

          {error && (
            <div
              className="rounded-lg p-4 mb-4"
              style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
            >
              {error}
            </div>
          )}

          {!isLoading && !error && clients.length === 0 && (
            <div
              className="border border-dashed rounded-lg p-12 text-center"
              style={{ borderColor: 'var(--border)' }}
            >
              <p style={{ color: 'var(--text-muted)' }}>No client tenants linked yet.</p>
            </div>
          )}

          {!isLoading && clients.length > 0 && (
            <div className="space-y-3">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="border rounded-lg p-4 flex items-center justify-between transition-colors"
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--card-bg)',
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold" style={{ color: 'var(--text)' }}>
                        {client.tenant.name}
                      </span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: 'var(--bg-muted)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {client.deliveryModel === 'MANAGED' ? 'Managed' : 'Self-Service'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                      <span>
                        {client.activeProjectCount} active project
                        {client.activeProjectCount !== 1 ? 's' : ''}
                      </span>
                      {gateHealthBadge(client.gateHealth, client.failingGateCount)}
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    <button
                      onClick={() => openApplyDefaultsModal(client)}
                      disabled={applyingDefaultsTo === client.clientTenantId}
                      className="px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text-muted)',
                        backgroundColor: 'transparent',
                        opacity: applyingDefaultsTo === client.clientTenantId ? 0.5 : 1,
                      }}
                    >
                      {applyingDefaultsTo === client.clientTenantId ? 'Applying...' :
                       applyDefaultsResult?.tenantId === client.clientTenantId
                         ? `Applied (${applyDefaultsResult.count} projects)`
                         : 'Apply Defaults'}
                    </button>
                    <button
                      onClick={() => handleSwitchContext(client.clientTenantId)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        border: '1px solid var(--primary)',
                        color: 'var(--primary-light)',
                        backgroundColor: 'transparent',
                      }}
                    >
                      Open &rarr;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Active Invitations + history */}
          <InvitationsPanel
            invites={invites}
            copiedId={copiedId}
            resendingId={resendingId}
            onCopy={copyToClipboard}
            onRevoke={handleRevokeInvite}
            onResend={handleResendInvite}
          />

          {resendToast && (
            <div
              role="status"
              style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: 'var(--card-bg)',
                border: '1px solid #22c55e',
                color: '#22c55e',
                padding: '12px 18px',
                borderRadius: 8,
                fontSize: 14,
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                zIndex: 60,
              }}
            >
              {resendToast}
            </div>
          )}

          {/* Invite New Client SlideOver */}
          <SlideOver
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            title="Invite New Client"
            width="md"
            footer={
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-sm rounded-lg"
                  style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInvite}
                  disabled={inviteSending}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                  style={{ backgroundColor: 'var(--primary)', opacity: inviteSending ? 0.6 : 1 }}
                >
                  {inviteSending ? 'Sending...' : 'Create Invite'}
                </button>
              </div>
            }
          >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                      Client Name <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={inviteForm.clientName}
                      onChange={e => setInviteForm(prev => ({ ...prev, clientName: e.target.value }))}
                      placeholder="Acme Corp"
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                      Client Email <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={inviteForm.clientEmail}
                      onChange={e => setInviteForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                      placeholder="admin@acme.com"
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                      Delivery Model
                    </label>
                    <select
                      value={inviteForm.deliveryModel}
                      onChange={e => setInviteForm(prev => ({ ...prev, deliveryModel: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    >
                      <option value="SELF_SERVICE">Self-Service</option>
                      <option value="MANAGED">Managed</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="override-permission" className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                      Client Override Permission
                    </label>
                    <p id="override-permission-desc" className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                      Controls how much clients can override your governance defaults.
                    </p>
                    <select
                      id="override-permission"
                      aria-describedby="override-permission-desc override-permission-helper"
                      value={inviteForm.overridePermission}
                      onChange={e => setInviteForm(prev => ({ ...prev, overridePermission: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    >
                      <option value="LOCKED">Locked — client cannot override defaults</option>
                      <option value="ALLOWED_WITH_REASON">Allowed with Reason (recommended)</option>
                      <option value="ALLOWED">Allowed — client has full control</option>
                    </select>
                    <p id="override-permission-helper" className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      {inviteForm.overridePermission === 'LOCKED'
                        ? 'Client cannot override any governance defaults. Best for regulated industries.'
                        : inviteForm.overridePermission === 'ALLOWED_WITH_REASON'
                        ? 'Client can override but must provide a reason. Reason is logged and you are notified.'
                        : 'Client can override any governance defaults freely. Best for technically capable clients.'}
                    </p>
                  </div>
                </div>
          </SlideOver>

          {/* Apply Defaults SlideOver */}
          <SlideOver
            isOpen={!!(applyDefaultsModal && governanceDefaults)}
            onClose={() => setApplyDefaultsModal(null)}
            title={applyDefaultsModal ? `Apply Governance Defaults to ${applyDefaultsModal.clientName}` : ''}
            subtitle="Review the defaults that will be applied."
            width="lg"
            footer={
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setApplyDefaultsModal(null)}
                  className="px-4 py-2 text-sm rounded-lg"
                  style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApplyDefaults}
                  disabled={!!applyingDefaultsTo}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                  style={{ backgroundColor: 'var(--primary)', opacity: applyingDefaultsTo ? 0.5 : 1 }}
                >
                  Apply Defaults &rarr;
                </button>
              </div>
            }
          >
            {applyDefaultsModal && governanceDefaults && (
              <>
                {/* Section A — What will be applied */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                    What will be applied
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {([
                        ['Approval mode', ({ ROLE_BASED: 'Role-Based', SINGLE_APPROVER: 'Single Approver' } as Record<string, string>)[governanceDefaults.defaultClientApprovalConfig.defaultApprovalMode ?? ''] ?? governanceDefaults.defaultClientApprovalConfig.defaultApprovalMode ?? 'Unknown'],
                        ['Revocation window', `${governanceDefaults.defaultClientApprovalConfig.revocationWindowHours ?? 24} hours`],
                        ['Acknowledgement window', `${governanceDefaults.defaultClientApprovalConfig.acknowledgementWindowHours ?? 24} hours`],
                      ] as const).map(([label, value]) => (
                        <tr key={label}>
                          <td style={{ padding: '6px 0', color: 'var(--text-muted)', whiteSpace: 'nowrap', paddingRight: 16 }}>{label}</td>
                          <td style={{ padding: '6px 0', color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* #3185 APPROVE.2 2c (ruling D13): Apply Defaults never changes a client's approval matrix. */}
                  <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }} data-testid="apply-defaults-matrix-note">
                    Client approval settings aren&rsquo;t changed by Apply Defaults. The client&rsquo;s workspace keeps its own.
                  </p>
                  {Object.keys(governanceDefaults.defaultRoleTierOverrides).length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Role-based approval matrix</p>
                      {BUSINESS_ROLES.filter(r => (governanceDefaults.defaultRoleTierOverrides[r.key] ?? []).length > 0).map(role => (
                        <div key={role.key} className="flex items-center gap-2 mb-1">
                          <span className="text-xs" style={{ color: 'var(--text-muted)', minWidth: 120 }}>{role.label}</span>
                          <div className="flex gap-1 flex-wrap">
                            {(governanceDefaults.defaultRoleTierOverrides[role.key] ?? []).map(art => (
                              <span key={art} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(124,58,237,0.1)', color: 'var(--primary)', border: '1px solid rgba(124,58,237,0.2)' }}>
                                {ARTEFACT_TYPE_OPTIONS.find(a => a.value === art)?.label ?? art}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Section B — Scope */}
                <div className="mb-5 p-3 rounded-lg" style={{ backgroundColor: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)' }}>
                  <p className="text-sm" style={{ color: 'var(--text)', margin: 0 }}>
                    These defaults will be applied to all <strong>{applyDefaultsModal.projectCount}</strong> existing
                    project{applyDefaultsModal.projectCount !== 1 ? 's' : ''} in <strong>{applyDefaultsModal.clientName}</strong> and
                    all future projects created in this tenant.
                  </p>
                </div>
              </>
            )}
          </SlideOver>

          {/* Partner Notification Configuration */}
          {notifConfig && (
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text)' }}>
                Notification Settings
              </h2>
              <div
                className="border rounded-lg p-5"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
              >
                <div className="space-y-4">
                  {([
                    { key: 'notifyOnGateFailure', label: 'Gate failures' },
                    { key: 'notifyOnArtefactApproval', label: 'Artefact approvals' },
                    { key: 'notifyOnPhaseHandoff', label: 'Phase handoffs' },
                    { key: 'notifyOnClientActivity', label: 'Client activity' },
                  ] as const).map(({ key, label }) => {
                    const val = notifConfig.configured
                      ? (notifConfig as any)[key]
                      : notifConfig.defaults?.[key] ?? true;
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm" style={{ color: 'var(--text)' }}>{label}</span>
                        <button
                          disabled={notifSaving}
                          onClick={async () => {
                            setNotifSaving(true);
                            try {
                              await apiFetch('/partner/notifications/config', {
                                method: 'PATCH',
                                body: JSON.stringify({ [key]: !val }),
                              });
                              setNotifConfig(prev => prev ? { ...prev, configured: true, [key]: !val } : prev);
                            } catch { /* ignore */ } finally { setNotifSaving(false); }
                          }}
                          className="text-xs px-3 py-1 rounded-full font-medium"
                          style={{
                            border: `1.5px solid ${val ? '#22c55e' : 'var(--border)'}`,
                            backgroundColor: val ? 'rgba(34,197,94,0.08)' : 'var(--surface)',
                            color: val ? '#22c55e' : 'var(--text-muted)',
                            cursor: 'pointer',
                          }}
                        >
                          {val ? 'On' : 'Off'}
                        </button>
                      </div>
                    );
                  })}

                  <hr style={{ borderColor: 'var(--border)', margin: '12px 0' }} />

                  <div>
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>
                      Digest Frequency
                    </p>
                    <div className="flex gap-2">
                      {(['REAL_TIME', 'HOURLY', 'DAILY'] as const).map(freq => {
                        const current = notifConfig.configured
                          ? notifConfig.digestFrequency
                          : notifConfig.defaults?.digestFrequency ?? 'DAILY';
                        const labels: Record<string, string> = { REAL_TIME: 'Real-Time', HOURLY: 'Hourly', DAILY: 'Daily' };
                        return (
                          <button
                            key={freq}
                            disabled={notifSaving}
                            onClick={async () => {
                              setNotifSaving(true);
                              try {
                                await apiFetch('/partner/notifications/config', {
                                  method: 'PATCH',
                                  body: JSON.stringify({ digestFrequency: freq }),
                                });
                                setNotifConfig(prev => prev ? { ...prev, configured: true, digestFrequency: freq } : prev);
                              } catch { /* ignore */ } finally { setNotifSaving(false); }
                            }}
                            className="px-3 py-1.5 text-sm rounded-lg"
                            style={{
                              border: current === freq ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                              backgroundColor: current === freq ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                              color: current === freq ? 'var(--primary)' : 'var(--text-muted)',
                              fontWeight: current === freq ? 600 : 500,
                              cursor: 'pointer',
                            }}
                          >
                            {labels[freq]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Governance Defaults Editor */}
          {governanceDefaults && !governanceLoading && (
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text)' }}>
                Governance Defaults
              </h2>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                These defaults apply when you use Apply Defaults on a client tenant. Edit them here without re-running the onboarding wizard.
              </p>
              <div
                className="border rounded-lg p-5"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
              >
                {/* Client Approval Requirements */}
                <div className="mb-5">
                  {/* #3185 APPROVE.2 2c (ruling D8) — the matrix replaces the
                      three require-client toggles. Sent only once edited or
                      already stored, so saving the windows alone doesn't create one. */}
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>
                    Client approvals
                  </h3>
                  <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
                    Copied into a new client&rsquo;s workspace when the client is linked. Later changes here, and Apply Defaults, don&rsquo;t change existing clients.
                  </p>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{D9_COPY}</p>
                  <PartnerClientApprovalMatrix
                    value={fullPartnerMatrix(editApproval.clientApprovalMatrix)}
                    onChange={(next) => setEditApproval(prev => ({ ...prev, clientApprovalMatrix: compactPartnerMatrix(next) }))}
                  />
                </div>

                {/* Default Windows */}
                <div className="mb-5">
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>
                    Default Windows
                  </h3>
                  <div className="flex gap-6 flex-wrap">
                    <div>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Revocation window</p>
                      <div className="flex gap-2">
                        {[24, 48, 72].map(h => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setEditApproval(prev => ({ ...prev, revocationWindowHours: h }))}
                            className="px-3 py-1 text-sm rounded-lg"
                            style={{
                              border: (editApproval.revocationWindowHours ?? 24) === h ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                              backgroundColor: (editApproval.revocationWindowHours ?? 24) === h ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                              color: (editApproval.revocationWindowHours ?? 24) === h ? 'var(--primary)' : 'var(--text-muted)',
                              cursor: 'pointer',
                            }}
                          >
                            {h}h
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Acknowledgement window</p>
                      <div className="flex gap-2">
                        {[24, 48, 72].map(h => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setEditApproval(prev => ({ ...prev, acknowledgementWindowHours: h }))}
                            className="px-3 py-1 text-sm rounded-lg"
                            style={{
                              border: (editApproval.acknowledgementWindowHours ?? 24) === h ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                              backgroundColor: (editApproval.acknowledgementWindowHours ?? 24) === h ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                              color: (editApproval.acknowledgementWindowHours ?? 24) === h ? 'var(--primary)' : 'var(--text-muted)',
                              cursor: 'pointer',
                            }}
                          >
                            {h}h
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Default Approval Mode */}
                <div className="mb-5">
                  <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>
                    Default Approval Mode
                  </h3>
                  <div className="flex gap-3">
                    {[
                      { value: 'SINGLE_APPROVER', label: 'Single Approver', desc: 'Any PRODUCT_MANAGER can approve.' },
                      { value: 'ROLE_BASED', label: 'Role-Based', desc: 'Enforces full role matrix.' },
                    ].map(opt => (
                      <label key={opt.value} className="flex items-start gap-2 p-3 rounded-lg flex-1" style={{
                        border: (editApproval.defaultApprovalMode ?? 'ROLE_BASED') === opt.value ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                        backgroundColor: (editApproval.defaultApprovalMode ?? 'ROLE_BASED') === opt.value ? 'rgba(124,58,237,0.08)' : 'transparent',
                        cursor: 'pointer',
                      }}>
                        <input
                          type="radio"
                          name="governance-approval-mode"
                          value={opt.value}
                          checked={(editApproval.defaultApprovalMode ?? 'ROLE_BASED') === opt.value}
                          onChange={() => setEditApproval(prev => ({ ...prev, defaultApprovalMode: opt.value }))}
                          style={{ accentColor: 'var(--primary)', marginTop: 2 }}
                        />
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--text)', margin: 0 }}>{opt.label}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)', margin: '2px 0 0' }}>{opt.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Business Tier Role Defaults */}
                <div className="mb-5">
                  <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                    Business Tier Role Defaults
                  </h3>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                    Configure which artefact types each business role can approve by default.
                  </p>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="text-xs text-left" style={{ padding: '6px 0', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Role</th>
                        <th className="text-xs text-left" style={{ padding: '6px 0', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>Can Approve</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BUSINESS_ROLES.map(role => (
                        <tr key={role.key}>
                          <td className="text-sm" style={{ padding: '8px 0', color: 'var(--text)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                            {role.label}
                          </td>
                          <td style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                            <div className="flex flex-wrap gap-1">
                              {ARTEFACT_TYPE_OPTIONS.map(art => {
                                const selected = (editRoleOverrides[role.key] ?? []).includes(art.value);
                                return (
                                  <button
                                    key={art.value}
                                    type="button"
                                    onClick={() => toggleRoleArtefact(role.key, art.value)}
                                    className="text-xs px-2 py-0.5 rounded"
                                    style={{
                                      border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
                                      backgroundColor: selected ? 'rgba(124,58,237,0.1)' : 'transparent',
                                      color: selected ? 'var(--primary)' : 'var(--text-muted)',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {art.label}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Save button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveGovernanceDefaults}
                    disabled={governanceSaving}
                    className="px-4 py-2 text-sm font-medium rounded-lg text-white"
                    style={{ backgroundColor: 'var(--primary)', opacity: governanceSaving ? 0.6 : 1 }}
                  >
                    {governanceSaving ? 'Saving...' : 'Save Defaults'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Governance toast */}
          {governanceToast && (
            <div
              role="status"
              style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: 'var(--card-bg)',
                border: '1px solid #22c55e',
                color: '#22c55e',
                padding: '12px 18px',
                borderRadius: 8,
                fontSize: 14,
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                zIndex: 60,
              }}
            >
              {governanceToast}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
